import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Input } from '@/common/ui/input'
import showErrorToast from '@/common/utils/showErrorToast'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'
import useMutationUpdatePlayer from '@/modules/manage-player/api/hooks/useMutationUpdatePlayer'
import { Edit } from 'lucide-react'

interface PlayerUpdateDiamondModalProps {
	player: {
		id: string
		phone: string
		diamondAmount: number
	}
}

export function PlayerUpdateDiamondModal({
	player,
}: PlayerUpdateDiamondModalProps) {
	const [open, setOpen] = useState(false)
	const [newDiamondAmount, setNewDiamondAmount] = useState('')
	const [confirmText, setConfirmText] = useState('')
	const isConfirmTextValid = confirmText === 'ยืนยัน'
	const isNewDiamondAmountValid =
		!isNaN(Number(newDiamondAmount)) && Number(newDiamondAmount) >= 0

	const { mutate: updatePlayer } = useMutationUpdatePlayer()
	const queryClient = useQueryClient()

	const handleConfirm = () => {
		if (!isConfirmTextValid || !isNewDiamondAmountValid) return

		try {
			updatePlayer(
				{
					id: player.id,
					payload: {
						diamondAmount: Number(newDiamondAmount),
					},
				},
				{
					onSuccess: () => {
						toast.success('อัพเดทจำนวนเพชรเรียบร้อยแล้ว')
						queryClient.invalidateQueries({
							queryKey: [PlayerQueryKeys.GetPlayerList],
						})
						queryClient.invalidateQueries({
							queryKey: [PlayerQueryKeys.GetPlayerDetail, player.id],
						})
						setNewDiamondAmount('')
						setConfirmText('')
						setOpen(false)
					},
					onError: (error) => {
						showErrorToast(error)
					},
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen)
		if (newOpen) {
			setNewDiamondAmount(player.diamondAmount.toString())
			setConfirmText('')
		}
	}

	return (
		<>
			<Button
				variant="ghost"
				size="icon"
				onClick={() => setOpen(true)}
				className="h-6 w-6"
			>
				<Edit className="h-4 w-4" />
			</Button>
			{open && (
				<Dialog open onOpenChange={handleOpenChange}>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>อัพเดทจำนวนเพชร</DialogTitle>
							<DialogDescription>
								คุณกำลังจะอัพเดทจำนวนเพชรของผู้เล่น
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-4 py-4">
							<div className="space-y-2">
								<p className="text-sm font-medium">เบอร์โทรศัพท์</p>
								<p className="text-sm text-muted-foreground">{player.phone}</p>
							</div>
							<div className="space-y-2">
								<p className="text-sm font-medium">Player ID</p>
								<p className="text-sm text-muted-foreground">{player.id}</p>
							</div>
							<div className="space-y-2">
								<p className="text-sm font-medium">จำนวนเพชรปัจจุบัน</p>
								<p className="text-sm text-muted-foreground">
									{player.diamondAmount?.toLocaleString('th-TH') || '0'} เพชร
								</p>
							</div>
							<div className="space-y-2">
								<p className="text-sm font-medium">จำนวนเพชรใหม่</p>
								<Input
									type="number"
									value={newDiamondAmount}
									onChange={(e) => setNewDiamondAmount(e.target.value)}
									placeholder="0"
									min={0}
								/>
							</div>
							<div className="space-y-2">
								<p className="text-sm font-medium">
									กรุณาพิมพ์ &quot;ยืนยัน&quot; เพื่อดำเนินการ
								</p>
								<Input
									value={confirmText}
									onChange={(e) => setConfirmText(e.target.value)}
									placeholder="ยืนยัน"
								/>
							</div>
						</div>

						<DialogFooter>
							<Button variant="outline" onClick={() => setOpen(false)}>
								ยกเลิก
							</Button>
							<Button
								variant="destructive"
								onClick={handleConfirm}
								disabled={!isConfirmTextValid || !isNewDiamondAmountValid}
							>
								ยืนยัน
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			)}
		</>
	)
}
